<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useCurrencyFormatter } from '../../../../utils/currencyFormatter';
import SellerExportPreviewModal from './SellerExportPreviewModal.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const { formatWithSymbol, getCurrentCurrency, onCurrencyChange, exchangeRates } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const props = defineProps({
    title: String,
    sellers: Object,
    coins: Object,
});

// State
const deleteSellerDialog = ref(false);
const deleteMultipleSellersDialog = ref(false);
const exportPreviewModal = ref();
const sellerToDelete = ref({});
const selectedSellers = ref([]);
const dt = ref();

// Computed pagination links
const links = computed(() => {
    if (!props.sellers?.links) return [];
    const _links = [...props.sellers.links];
    _links.shift();
    _links.pop();
    return _links;
});

function toggleAllActive() {
    const activeSellers = props.sellers?.data?.filter(seller => !isSellerDeleted(seller)) || [];
    const allActive = activeSellers.length > 0 && activeSellers.every(seller => selectedSellers.value.includes(seller));
    
    if (allActive) {
        selectedSellers.value = selectedSellers.value.filter(selected => isSellerDeleted(selected));
    } else {
        const deletedSelected = selectedSellers.value.filter(selected => isSellerDeleted(selected));
        selectedSellers.value = [...deletedSelected, ...activeSellers];
    }
}

// Navigation functions
function addSeller() {
    router.visit(route('sellers.create'), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
}

function viewSeller(seller) {
    router.visit(route('sellers.show', seller.id), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
}

function editSeller(seller) {
    router.visit(route('sellers.edit', seller.id), {
        onStart: () => showLoading($t('Loading...')),
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
}

function confirmDeleteSeller(seller) {
    sellerToDelete.value = seller;
    deleteSellerDialog.value = true;
}

function deleteSeller() {
    router.delete(route('sellers.destroy', sellerToDelete.value.id), {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading($t('Deleting seller...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            deleteSellerDialog.value = false;
            sellerToDelete.value = {};
            toast.add({
                severity: 'success',
                summary: $t('Successful'),
                detail: $t('Seller deleted successfully'),
                life: 3000
            });
        },
        onError: () => {
            hideLoading();
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to delete seller'),
                life: 3000
            });
        }
    });
}

function confirmDeleteSelected() {
    deleteMultipleSellersDialog.value = true;
}

function deleteSelectedSellers() {
    // Implement batch delete logic here
    const sellerIds = selectedSellers.value.map(s => s.id);

    router.delete(route('sellers.bulk-destroy'), {
        data: { ids: sellerIds }
    }, {
        preserveScroll: true,
        preserveState: true,
        onStart: () => showLoading($t('Deleting sellers...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            deleteMultipleSellersDialog.value = false;
            selectedSellers.value = [];
            toast.add({
                severity: 'success',
                summary: $t('Successful'),
                detail: $t('Sellers deleted successfully'),
                life: 3000
            });
        },
        onError: () => {
            hideLoading();
        }
    });
}

function exportCSV() {
    dt.value.exportCSV();
}

function openExportPreview() {
    exportPreviewModal.value.open();
}

// Format commission display
function formatCommission(seller) {
    if (!seller.comission) return '-';

    const price = seller.comission / 100;
    const fromCurrency = seller.comission_coin || 'USD';
    const toCurrency = currentCurrency.value;

    const fromRate = exchangeRates.value[fromCurrency] || 1;
    const toRate = exchangeRates.value[toCurrency] || 1;

    const usdValue = price / fromRate;
    const convertedValue = usdValue * toRate;

    return formatWithSymbol(convertedValue, toCurrency);
}

// Format date function
function formatDate(dateString) {
    if (!dateString) return $t('Never');
    return new Date(dateString).toLocaleString();
}

function isSellerDeleted(seller) {
    return seller.deleted_at !== null;
}

defineOptions({
    layout: AdminMainLayout,
});

onMounted(() => {
    onCurrencyChange(({ currency }) => {
        currentCurrency.value = currency;
    });

    currentCurrency.value = getCurrentCurrency();
});
</script>

<template>
    <div>
        <!-- Header Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-users text-2xl text-primary-500"></i>
                        <div>
                            <h2 class="text-xl font-bold">{{ $t('Sellers') }}</h2>
                            <span class="text-sm text-surface-500">{{ $t('Manage website sellers and consultants') }}</span>
                        </div>
                    </div>
                    <Button 
                        :label="$t('Add seller')" 
                        icon="pi pi-plus" 
                        severity="primary" 
                        @click="addSeller" 
                    />
                </div>
            </template>
        </Card>

        <!-- Main Content Card -->
        <Card>
            <template #content>
                <Toolbar class="mb-4">
                    <template #start>
                        <Button 
                            label="Delete" 
                            icon="pi pi-trash" 
                            severity="danger" 
                            @click="confirmDeleteSelected" 
                            :disabled="!selectedSellers || !selectedSellers.length" 
                        />
                    </template>

                    <template #end>
                        <Button 
                            :label="$t('Export')" 
                            icon="pi pi-upload" 
                            severity="secondary" 
                            @click="openExportPreview" 
                        />
                    </template>
                </Toolbar>

                <DataTable
                    ref="dt"
                    v-model:selection="selectedSellers"
                    :value="sellers?.data || []"
                    dataKey="id"
                    :paginator="false"
                    :rows="10"
                    responsiveLayout="scroll"
                >
                    <template #header>
                        <div class="flex flex-wrap gap-2 items-center justify-between">
                            <h4 class="m-0">{{ $t('Sellers list') }}</h4>
                            <span class="text-sm text-surface-500">
                                {{ $t('Total') }}: {{ sellers?.total || 0 }} {{ $t('sellers') }}
                            </span>
                        </div>
                    </template>

                    <template #empty>
                        <div class="text-center py-8">
                            <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                            <p class="text-surface-500">{{ $t('No sellers found') }}</p>
                            <Button 
                                :label="$t('Add first seller')" 
                                icon="pi pi-plus" 
                                class="mt-3"
                                @click="addSeller" 
                            />
                        </div>
                    </template>

                    <Column style="width: 3rem" :exportable="false">
                        <template #header>
                            <Checkbox @click="toggleAllActive" />
                        </template>
                        <template #body="slotProps">
                            <Checkbox
                                :value="slotProps.data"
                                v-model="selectedSellers"
                                :disabled="isSellerDeleted(slotProps.data)"
                                :class="{ 'opacity-30': isSellerDeleted(slotProps.data) }"
                            />
                        </template>
                    </Column>
                    
                    <Column field="name" :header="$t('Name')" sortable style="min-width: 14rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2" :class="{ 'opacity-50': isSellerDeleted(slotProps.data) }">
                                <Avatar 
                                    :label="slotProps.data.name?.charAt(0)?.toUpperCase() || 'S'" 
                                    size="small"
                                    shape="circle"
                                    :style="{ 
                                        backgroundColor: isSellerDeleted(slotProps.data) ? '#6b7280' : '#2563eb', 
                                        color: 'white' 
                                    }"
                                />
                                <div>
                                    <div class="flex items-center gap-2">
                                        <p class="font-medium text-surface-900" :class="{ 'line-through': isSellerDeleted(slotProps.data) }">
                                            {{ slotProps.data.name }}
                                        </p>
                                        <Tag v-if="isSellerDeleted(slotProps.data)" value="Deleted" severity="danger" class="text-xs" />
                                    </div>
                                    <p class="text-sm text-surface-500">ID: {{ slotProps.data.id }}</p>
                                </div>
                            </div>
                        </template>
                    </Column>
                    
                    <Column field="email" :header="$t('Email')" sortable style="min-width: 16rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2" :class="{ 'opacity-50': isSellerDeleted(slotProps.data) }">
                                <i class="pi pi-envelope text-surface-400"></i>
                                <span :class="{ 'line-through': isSellerDeleted(slotProps.data) }">
                                    {{ slotProps.data.email || '-' }}
                                </span>
                            </div>
                        </template>
                    </Column>
                    
                    <!-- <Column field="comission" :header="$t('Commission')" sortable style="min-width: 8rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                
                                 <span>{{ formatCommission(slotProps.data) }}</span>
                            </div>
                        </template>
                    </Column> -->
                    
                    <Column field="active_sites_count" :header="$t('Active sites')" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            <Tag
                                :value="String(slotProps.data.active_sites_count ?? 0)"
                                :severity="(slotProps.data.active_sites_count ?? 0) > 0 ? 'success' : 'secondary'"
                            />
                        </template>
                    </Column>

                    <Column field="created_at" :header="$t('Created')" sortable style="min-width: 10rem">
                        <template #body="slotProps">
                            <span class="text-sm" :class="{ 'opacity-50 line-through': isSellerDeleted(slotProps.data) }">
                                {{ formatDate(slotProps.data.created_at) }}
                            </span>
                        </template>
                    </Column>
                    
                    <Column :header="$t('Actions')" :exportable="false" style="min-width: 8rem">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                <Button 
                                    icon="pi pi-eye" 
                                    severity="info"
                                    text
                                    rounded
                                    v-tooltip.top="isSellerDeleted(slotProps.data) ? $t('Seller is deleted') : $t('View')"
                                    @click="viewSeller(slotProps.data)" 
                                    :disabled="isSellerDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isSellerDeleted(slotProps.data) }"
                                />
                                <Button 
                                    icon="pi pi-pencil" 
                                    severity="secondary"
                                    text
                                    rounded
                                    v-tooltip.top="isSellerDeleted(slotProps.data) ? $t('Seller is deleted') : $t('Edit')"
                                    @click="editSeller(slotProps.data)" 
                                    :disabled="isSellerDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isSellerDeleted(slotProps.data) }"
                                />
                                <Button 
                                    icon="pi pi-trash" 
                                    severity="danger"
                                    text
                                    rounded
                                    v-tooltip.top="isSellerDeleted(slotProps.data) ? $t('Already deleted') : $t('Delete')"
                                    @click="confirmDeleteSeller(slotProps.data)" 
                                    :disabled="isSellerDeleted(slotProps.data)"
                                    :class="{ 'opacity-30': isSellerDeleted(slotProps.data) }"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>

                <!-- Custom Pagination for compatibility with Laravel -->
                <div class="flex items-center justify-between mt-4" v-if="sellers?.links">
                    <div>
                        <span class="text-sm text-surface-500">
                            {{ $t('Showing') }} {{ sellers.from || 0 }} {{ $t('to') }} {{ sellers.to || 0 }} 
                            {{ $t('of') }} {{ sellers.total || 0 }} {{ $t('results') }}
                        </span>
                    </div>
                    <div class="flex gap-1">
                        <Button
                            v-if="sellers.prev_page_url"
                            icon="pi pi-chevron-left"
                            severity="secondary"
                            text
                            @click="() => {
                                router.visit(sellers.prev_page_url, {
                                    onStart: () => showLoading($t('Loading previous page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }"
                        />
                        <Button
                            v-for="link in links"
                            :key="link.label"
                            :label="link.label"
                            :severity="link.active ? 'primary' : 'secondary'"
                            :text="!link.active"
                            @click="() => {
                                router.visit(link.url, {
                                    onStart: () => showLoading($t('Loading page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }"
                            :disabled="!link.url"
                        />
                        <Button
                            v-if="sellers.next_page_url"
                            icon="pi pi-chevron-right"
                            severity="secondary"
                            text
                            @click="() => {
                                router.visit(sellers.next_page_url, {
                                    onStart: () => showLoading($t('Loading next page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <!-- Export Preview Modal -->
        <SellerExportPreviewModal ref="exportPreviewModal" />

        <!-- Delete Seller Dialog -->
        <Dialog 
            v-model:visible="deleteSellerDialog" 
            :style="{ width: '450px' }" 
            :header="$t('Confirm')" 
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span v-if="sellerToDelete">
                    {{ $t('Are you sure you want to delete seller') }} <b>{{ sellerToDelete.name }}</b>?
                </span>
            </div>
            <template #footer>
                <Button 
                    :label="$t('No')" 
                    icon="pi pi-times" 
                    text 
                    @click="deleteSellerDialog = false" 
                />
                <Button 
                    :label="$t('Yes')" 
                    icon="pi pi-check" 
                    severity="danger"
                    @click="deleteSeller" 
                />
            </template>
        </Dialog>

        <!-- Delete Multiple Sellers Dialog -->
        <Dialog 
            v-model:visible="deleteMultipleSellersDialog" 
            :style="{ width: '450px' }" 
            :header="$t('Confirm')" 
            :modal="true"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
                <span>{{ $t('Are you sure you want to delete the selected sellers?') }}</span>
            </div>
            <template #footer>
                <Button 
                    :label="$t('No')" 
                    icon="pi pi-times" 
                    text 
                    @click="deleteMultipleSellersDialog = false" 
                />
                <Button 
                    :label="$t('Yes')" 
                    icon="pi pi-check" 
                    severity="danger"
                    @click="deleteSelectedSellers" 
                />
            </template>
        </Dialog>
    </div>
</template>